/**
 * SVG Path Utilities
 * Utilities for parsing SVG paths and analyzing control points
 */

// Normalized SVG viewBox size (normalized SVGs have viewBox="0 0 100 100")
const NORMALIZED_VIEWBOX_SIZE = 100;
const NORMALIZED_VIEWBOX_CENTER = NORMALIZED_VIEWBOX_SIZE / 2; // 50

/**
 * Parse SVG path d attribute to extract all points
 * @param {string} pathD - The path d attribute value
 * @returns {Array} Array of points with {x, y, type}
 */
export const parsePathPoints = (pathD) => {
  if (!pathD) return [];
  
  const points = [];
  const commands = pathD.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  
  if (!commands) return [];
  
  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  
  commands.forEach(command => {
    const type = command[0];
    const coords = command.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    
    switch(type.toUpperCase()) {
      case 'M': // Move to
        if (coords.length >= 2) {
          currentX = coords[0];
          currentY = coords[1];
          startX = currentX;
          startY = currentY;
          points.push({ x: currentX, y: currentY, type: 'M' });
        }
        break;
      case 'L': // Line to
        if (coords.length >= 2) {
          currentX = coords[0];
          currentY = coords[1];
          points.push({ x: currentX, y: currentY, type: 'L' });
        }
        break;
      case 'H': // Horizontal line
        if (coords.length >= 1) {
          currentX = coords[0];
          points.push({ x: currentX, y: currentY, type: 'H' });
        }
        break;
      case 'V': // Vertical line
        if (coords.length >= 1) {
          currentY = coords[0];
          points.push({ x: currentX, y: currentY, type: 'V' });
        }
        break;
      case 'C': // Cubic Bezier - take start and end points
        if (coords.length >= 6) {
          currentX = coords[4];
          currentY = coords[5];
          points.push({ x: coords[0], y: coords[1], type: 'C-start' });
          points.push({ x: coords[2], y: coords[3], type: 'C-control' });
          points.push({ x: currentX, y: currentY, type: 'C-end' });
        }
        break;
      case 'S': // Smooth cubic Bezier
        if (coords.length >= 4) {
          currentX = coords[2];
          currentY = coords[3];
          points.push({ x: currentX, y: currentY, type: 'S' });
        }
        break;
      case 'Q': // Quadratic Bezier
        if (coords.length >= 4) {
          currentX = coords[2];
          currentY = coords[3];
          points.push({ x: coords[0], y: coords[1], type: 'Q-control' });
          points.push({ x: currentX, y: currentY, type: 'Q-end' });
        }
        break;
      case 'T': // Smooth quadratic Bezier
        if (coords.length >= 2) {
          currentX = coords[0];
          currentY = coords[1];
          points.push({ x: currentX, y: currentY, type: 'T' });
        }
        break;
      case 'Z':
      case 'z': // Close path
        currentX = startX;
        currentY = startY;
        break;
    }
  });
  
  return points;
};

/**
 * Find nearest point in path to a given point
 * @param {Object} targetPoint - {x, y} coordinates
 * @param {Array} pathPoints - Array of path points
 * @returns {Object|null} Nearest point with distance, or null
 */
export const findNearestPathPoint = (targetPoint, pathPoints) => {
  if (!pathPoints || pathPoints.length === 0) return null;
  
  let nearest = null;
  let minDistance = Infinity;
  
  pathPoints.forEach(pathPoint => {
    const distance = Math.sqrt(
      Math.pow(targetPoint.x - pathPoint.x, 2) + 
      Math.pow(targetPoint.y - pathPoint.y, 2)
    );
    
    if (distance < minDistance) {
      minDistance = distance;
      nearest = { ...pathPoint, distance };
    }
  });
  
  return nearest;
};

/**
 * Analyze SVG for Test 1: Find snap points and their nearest path points
 * @param {Object} options - Analysis options
 * @param {string} options.svgContent - SVG content string
 * @param {Array} options.snapPoints - Array of snap points {id, x, y, color, type}
 * @returns {Object} Analysis result
 */
export const test1AnalyzeSvg = ({ svgContent, snapPoints }) => {
  if (!svgContent) {
    return {
      error: 'Không tìm thấy SVG content',
      snapPoints: [],
      totalPathPoints: 0
    };
  }

  // Filter only Snap Point type
  const filteredSnapPoints = snapPoints.filter(point => point.type === 'Snap Point');

  if (filteredSnapPoints.length === 0) {
    return {
      snapPoints: [],
      totalPathPoints: 0
    };
  }

  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const paths = svgDoc.querySelectorAll('path');
    
    const allPathPoints = [];
    paths.forEach(path => {
      const pathD = path.getAttribute('d');
      if (pathD) {
        const points = parsePathPoints(pathD);
        allPathPoints.push(...points);
      }
    });
    
    // Find nearest path points for each snap point
    const snapPointResults = filteredSnapPoints.map(snapPoint => {
      const nearest = findNearestPathPoint(
        { x: snapPoint.x, y: snapPoint.y },
        allPathPoints
      );
      
      return {
        snapPoint: {
          id: snapPoint.id,
          x: snapPoint.x,
          y: snapPoint.y,
          color: snapPoint.color
        },
        nearestPathPoint: nearest
      };
    });
    
    return {
      snapPoints: snapPointResults,
      totalPathPoints: allPathPoints.length
    };
  } catch (error) {
    console.error('Error processing SVG:', error);
    return {
      error: error.message,
      snapPoints: [],
      totalPathPoints: 0
    };
  }
};

/**
 * Calculate distance between two points
 * @param {Object} point1 - {x, y}
 * @param {Object} point2 - {x, y}
 * @returns {number} Distance
 */
export const calculateDistance = (point1, point2) => {
  return Math.sqrt(
    Math.pow(point2.x - point1.x, 2) + 
    Math.pow(point2.y - point1.y, 2)
  );
};

/**
 * Calculate scale and translate for SVG2 based on SVG1
 * @param {Object} svg1Data - SVG1 analysis data {snapPoints, scale, center_x, center_y}
 * @param {Object} svg2Data - SVG2 analysis data {snapPoints, scale, center_x, center_y}
 * @returns {Object|null} Calculated transform {scale2, cx2, cy2} or null if insufficient data
 */
export const calculateSvg2Transform = (svg1Data, svg2Data) => {
  // Need at least 2 snap points with nearest path points for each SVG
  if (!svg1Data.snapPoints || svg1Data.snapPoints.length < 2) {
    return null;
  }
  if (!svg2Data.snapPoints || svg2Data.snapPoints.length < 2) {
    return null;
  }

  // Get all nearest path points from snap points
  const svg1PathPoints = svg1Data.snapPoints
    .map(sp => sp.nearestPathPoint)
    .filter(pp => pp != null);
  
  const svg2PathPoints = svg2Data.snapPoints
    .map(sp => sp.nearestPathPoint)
    .filter(pp => pp != null);

  if (svg1PathPoints.length < 2 || svg2PathPoints.length < 2) {
    return null;
  }

  // Find the leftmost point (smallest x) for each SVG
  const svg1Leftmost = svg1PathPoints.reduce((leftmost, point) => 
    point.x < leftmost.x ? point : leftmost
  );
  const svg2Leftmost = svg2PathPoints.reduce((leftmost, point) => 
    point.x < leftmost.x ? point : leftmost
  );

  // Get another point (second smallest x or just different from leftmost) for length calculation
  // We'll use the second smallest x, or if all same x, use smallest y
  const svg1OtherPoints = svg1PathPoints.filter(p => p !== svg1Leftmost);
  const svg2OtherPoints = svg2PathPoints.filter(p => p !== svg2Leftmost);

  if (svg1OtherPoints.length === 0 || svg2OtherPoints.length === 0) {
    return null;
  }

  // For length calculation, use the point with second smallest x, or just pick one
  const svg1Point2 = svg1OtherPoints.reduce((min, point) => 
    point.x < min.x ? point : (point.x === min.x && point.y < min.y ? point : min),
    svg1OtherPoints[0]
  );
  
  const svg2Point2 = svg2OtherPoints.reduce((min, point) => 
    point.x < min.x ? point : (point.x === min.x && point.y < min.y ? point : min),
    svg2OtherPoints[0]
  );

  // Calculate lengths
  const length1 = calculateDistance(svg1Leftmost, svg1Point2);
  const length2 = calculateDistance(svg2Leftmost, svg2Point2);

  if (length2 === 0) {
    return null;
  }

  // Get current scales (default to 1 if not provided)
  const scale1 = svg1Data.scale || 1.0;

  // Calculate new scale for SVG2
  // scale2_new = (length1 / length2) * scale1
  const newScale2 = (length1 / length2) * scale1;

  // Get nearest path points (p1 for SVG1, p2 for SVG2)
  // p1 is the leftmost point in SVG1, p2 is the leftmost point in SVG2
  const p1 = svg1Leftmost;
  const p2 = svg2Leftmost;

  // Get current center positions
  const svg1CenterX = svg1Data.center_x || 0;
  const svg1CenterY = svg1Data.center_y || 0;

  // Calculate translate offset in design space
  // Normalized SVG has viewBox="0 0 100 100", so center is at (50, 50)
  // To calculate position relative to center: (point.x - NORMALIZED_VIEWBOX_CENTER) * scale
  //
  // We want p2 (SVG2's nearest path point) to align with p1 (SVG1's nearest path point)
  // After scaling, the offset from center for each point:
  // - SVG1: (p1.x - NORMALIZED_VIEWBOX_CENTER) * scale1 (distance from SVG1's center in design space)
  // - SVG2: (p2.x - NORMALIZED_VIEWBOX_CENTER) * newScale2 (distance from SVG2's center in design space after new scale)
  //
  // To align p2 with p1, we need to offset SVG2's center by:
  // svgOffsetX = (p1.x - NORMALIZED_VIEWBOX_CENTER) * scale1 - (p2.x - NORMALIZED_VIEWBOX_CENTER) * newScale2
  // This calculates the difference in design space between the two points' positions from their centers
  const svgOffsetX = (p1.x - NORMALIZED_VIEWBOX_CENTER) * scale1 - (p2.x - NORMALIZED_VIEWBOX_CENTER) * newScale2;
  const svgOffsetY = (p1.y - NORMALIZED_VIEWBOX_CENTER) * scale1 - (p2.y - NORMALIZED_VIEWBOX_CENTER) * newScale2;

  // Apply the offset to SVG1's center position to get SVG2's new center
  // After this translation, p2 will align with p1 in design space
  const newCx2 = svg1CenterX + svgOffsetX;
  const newCy2 = svg1CenterY + svgOffsetY;

  return {
    scale2: newScale2,
    cx2: newCx2,
    cy2: newCy2,
    length1,
    length2,
    ratio: length1 / length2
  };
};

/**
 * Get all path elements from SVG content
 * @param {string} svgContent - SVG content string
 * @returns {Array} Array of path elements with {element, d}
 */
export const getPathsFromSvg = (svgContent) => {
  if (!svgContent) return [];
  
  try {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const paths = svgDoc.querySelectorAll('path');
    
    return Array.from(paths).map(path => ({
      element: path,
      d: path.getAttribute('d') || ''
    }));
  } catch (error) {
    console.error('Error parsing SVG:', error);
    return [];
  }
};

/**
 * Find which path contains a given point (within tolerance)
 * @param {Array} paths - Array of paths from getPathsFromSvg
 * @param {Object} targetPoint - {x, y} point to find
 * @param {number} tolerance - Distance tolerance (default: 0.1)
 * @returns {Object|null} Path and the matched point, or null
 */
export const findPathContainingPoint = (paths, targetPoint, tolerance = 0.1) => {
  for (const pathInfo of paths) {
    const pathPoints = parsePathPoints(pathInfo.d);
    
    for (const point of pathPoints) {
      const distance = calculateDistance(targetPoint, point);
      if (distance <= tolerance) {
        return {
          pathInfo,
          matchedPoint: point,
          allPoints: pathPoints
        };
      }
    }
  }
  return null;
};

/**
 * Cut path from a specific point - returns path that starts from the cut point
 * @param {string} pathD - Path d attribute
 * @param {Object} cutPoint - {x, y} point to cut from
 * @param {boolean} includeCutPoint - Whether to include the cut point in result (default: true)
 * @returns {string} New path d that starts from cut point
 */
export const cutPathFromPoint = (pathD, cutPoint, includeCutPoint = true) => {
  if (!pathD || !cutPoint) return pathD;
  
  const commands = pathD.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!commands || commands.length === 0) return pathD;
  
  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  let resultCommands = [];
  let foundCutPoint = false;
  const tolerance = 0.01;
  
  for (const command of commands) {
    const type = command[0];
    const coords = command.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    
    switch(type.toUpperCase()) {
      case 'M':
        if (coords.length >= 2) {
          currentX = coords[0];
          currentY = coords[1];
          startX = currentX;
          startY = currentY;
          
          if (Math.abs(currentX - cutPoint.x) < tolerance && Math.abs(currentY - cutPoint.y) < tolerance) {
            foundCutPoint = true;
            if (includeCutPoint) {
              resultCommands.push(`M ${currentX},${currentY}`);
            }
          }
        }
        break;
      case 'L':
        if (coords.length >= 2) {
          const endX = coords[0];
          const endY = coords[1];
          const distToCut = calculateDistance(cutPoint, {x: endX, y: endY});
          
          if (distToCut < tolerance) {
            foundCutPoint = true;
            if (includeCutPoint) {
              resultCommands.push(`M ${cutPoint.x},${cutPoint.y}`);
            }
          } else if (foundCutPoint) {
            resultCommands.push(command);
          }
          currentX = endX;
          currentY = endY;
        }
        break;
      // Handle other commands similarly
      default:
        if (foundCutPoint) {
          resultCommands.push(command);
          if (coords.length >= 2) {
            currentX = coords[coords.length - 2];
            currentY = coords[coords.length - 1];
          }
        } else {
          // Update current position even if not including in result
          if (coords.length >= 2) {
            currentX = coords[coords.length - 2];
            currentY = coords[coords.length - 1];
          }
        }
        break;
    }
  }
  
  return resultCommands.join(' ');
};

/**
 * Cut path at a specific point - reorder path to end at cutPoint
 * p1 (cutPoint) already exists in the path, we just need to reorder
 * Example: A B C D E F p1 q1 G H I --> q1 G H I A B C D E F p1
 * @param {string} pathD - Path d attribute
 * @param {Object} cutPoint - {x, y} point to end at (p1)
 * @param {boolean} includeCutPoint - Whether to include the cut point in result (default: true)
 * @returns {string} New path d that ends at cut point (reordered)
 */
export const cutPathAtPoint = (pathD, cutPoint, includeCutPoint = true) => {
  if (!pathD || !cutPoint) return pathD;
  
  const commands = pathD.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!commands || commands.length === 0) return pathD;
  
  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  const tolerance = 0.05;
  
  // Find the command index that contains cutPoint (p1)
  let p1CommandIndex = -1;
  
  for (let i = 0; i < commands.length; i++) {
    const command = commands[i];
    const type = command[0];
    const coords = command.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    
    let endX = currentX;
    let endY = currentY;
    
    switch(type.toUpperCase()) {
      case 'M':
        if (coords.length >= 2) {
          endX = coords[0];
          endY = coords[1];
          currentX = endX;
          currentY = endY;
          startX = endX;
          startY = endY;
        }
        break;
      case 'L':
        if (coords.length >= 2) {
          endX = coords[0];
          endY = coords[1];
        }
        break;
      case 'H':
        if (coords.length >= 1) {
          endX = coords[0];
          endY = currentY;
        }
        break;
      case 'V':
        if (coords.length >= 1) {
          endX = currentX;
          endY = coords[0];
        }
        break;
      case 'Z':
      case 'z':
        endX = startX;
        endY = startY;
        break;
      case 'C':
        if (coords.length >= 6) {
          endX = coords[4];
          endY = coords[5];
        }
        break;
      case 'S':
      case 'Q':
        if (coords.length >= 4) {
          endX = coords[2];
          endY = coords[3];
        }
        break;
      case 'T':
        if (coords.length >= 2) {
          endX = coords[0];
          endY = coords[1];
        }
        break;
    }
    
    // Check if this command's endpoint matches cutPoint (p1)
    const distToCut = calculateDistance(cutPoint, {x: endX, y: endY});
    if (distToCut < tolerance) {
      p1CommandIndex = i;
      break;
    }
    
    // Update current position
    currentX = endX;
    currentY = endY;
  }
  
  if (p1CommandIndex === -1) {
    // p1 not found, return original path
    return pathD;
  }
  
  // Split commands into two parts:
  // - beforeP1: commands from start to p1 (including p1)
  // - afterP1: commands after p1 (starting from q1)
  const beforeP1 = commands.slice(0, p1CommandIndex + 1);
  const afterP1 = commands.slice(p1CommandIndex + 1);
  
  // Remove Z command if it exists
  const beforeP1NoZ = beforeP1.filter(cmd => cmd[0].toUpperCase() !== 'Z');
  const afterP1NoZ = afterP1.filter(cmd => cmd[0].toUpperCase() !== 'Z');
  
  // Get the first command's endpoint from afterP1 (this is q1)
  // We need to create a new M command for q1 if afterP1 is not empty
  let reorderedCommands = [];
  
  if (afterP1NoZ.length > 0) {
    // Get q1 coordinates from the first command in afterP1
    const firstAfterP1Command = afterP1NoZ[0];
    const firstAfterType = firstAfterP1Command[0];
    const firstAfterCoords = firstAfterP1Command.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    
    let q1X, q1Y;
    
    // Calculate q1 based on command type
    switch(firstAfterType.toUpperCase()) {
      case 'M':
        if (firstAfterCoords.length >= 2) {
          q1X = firstAfterCoords[0];
          q1Y = firstAfterCoords[1];
        }
        break;
      case 'L':
      case 'C':
      case 'S':
      case 'Q':
      case 'T':
        if (firstAfterCoords.length >= 2) {
          // For these commands, get endpoint
          if (firstAfterType.toUpperCase() === 'C' && firstAfterCoords.length >= 6) {
            q1X = firstAfterCoords[4];
            q1Y = firstAfterCoords[5];
          } else if (firstAfterType.toUpperCase() === 'Q' && firstAfterCoords.length >= 4) {
            q1X = firstAfterCoords[2];
            q1Y = firstAfterCoords[3];
          } else {
            q1X = firstAfterCoords[0];
            q1Y = firstAfterCoords[1];
          }
        }
        break;
      default:
        // For other commands, try to get first point
        if (firstAfterCoords.length >= 2) {
          q1X = firstAfterCoords[0];
          q1Y = firstAfterCoords[1];
        }
    }
    
    if (q1X !== undefined && q1Y !== undefined) {
      // Start with M command to q1, then rest of afterP1, then beforeP1
      reorderedCommands.push(`M ${q1X},${q1Y}`);
      reorderedCommands.push(...afterP1NoZ.slice(1)); // Skip first command since we created M
      reorderedCommands.push(...beforeP1NoZ);
    } else {
      // Fallback: just concatenate as is
      reorderedCommands = [...afterP1NoZ, ...beforeP1NoZ];
    }
  } else {
    // No afterP1, just return beforeP1
    reorderedCommands = [...beforeP1NoZ];
  }
  
  return reorderedCommands.join(' ');
};

/**
 * Convert path from SVG coordinates to absolute design coordinates
 * @param {string} pathD - Path d attribute
 * @param {number} centerX - Center X in design space
 * @param {number} centerY - Center Y in design space
 * @param {number} scale - Scale factor
 * @returns {string} Path d in absolute coordinates
 */
export const convertPathToAbsolute = (pathD, centerX, centerY, scale) => {
  if (!pathD) return '';
  
  // For normalized SVG (viewBox 0 0 100 100), convert coordinates:
  // absoluteX = centerX + (svgX - 50) * scale
  // absoluteY = centerY + (svgY - 50) * scale
  
  const commands = pathD.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!commands) return pathD;
  
  const absoluteCommands = commands.map(command => {
    const type = command[0];
    const isUpperCase = type === type.toUpperCase();
    const coords = command.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    
    // Convert coordinates
    const absoluteCoords = [];
    for (let i = 0; i < coords.length; i += 2) {
      if (i + 1 < coords.length) {
        const x = coords[i];
        const y = coords[i + 1];
        const absX = centerX + (x - NORMALIZED_VIEWBOX_CENTER) * scale;
        const absY = centerY + (y - NORMALIZED_VIEWBOX_CENTER) * scale;
        absoluteCoords.push(absX.toFixed(2), absY.toFixed(2));
      } else {
        absoluteCoords.push(coords[i]);
      }
    }
    
    return `${type.toUpperCase()} ${absoluteCoords.join(' ')}`;
  });
  
  return absoluteCommands.join(' ');
};

/**
 * Merge two paths: PATH1 ends at P1, PATH2 starts at P2 (where P1 = P2)
 * @param {string} path1Absolute - First path in absolute coordinates (ends at P1)
 * @param {string} path2Absolute - Second path in absolute coordinates (starts at P2)
 * @returns {string} Merged path
 */
export const mergePaths = (path1Absolute, path2Absolute) => {
  if (!path1Absolute && !path2Absolute) return '';
  if (!path1Absolute) return path2Absolute;
  if (!path2Absolute) return path1Absolute;
  
  // Since P1 = P2, we can directly concatenate
  // Remove any 'Z' or 'z' from path1 if present, and ensure path2 starts properly
  const path1Clean = path1Absolute.trim().replace(/[Zz]\s*$/, '');
  const path2Clean = path2Absolute.trim();
  
  // If path2 starts with M (move), we can keep it, or replace with L if we want to connect
  // For now, we'll keep path2 as is since it already starts at P2
  
  return `${path1Clean} ${path2Clean}`;
};

/**
 * Merge SVG paths based on snap points
 * @param {Object} options
 * @param {string} options.svg1Content - SVG1 content
 * @param {string} options.svg2Content - SVG2 content
 * @param {Object} options.p1 - {x, y} nearest path point in SVG1 (leftmost)
 * @param {Object} options.q1 - {x, y} second nearest path point in SVG1
 * @param {Object} options.p2 - {x, y} nearest path point in SVG2 (leftmost)
 * @param {Object} options.q2 - {x, y} second nearest path point in SVG2
 * @param {number} options.svg1CenterX - SVG1 center X in design space
 * @param {number} options.svg1CenterY - SVG1 center Y in design space
 * @param {number} options.svg1Scale - SVG1 scale
 * @param {number} options.svg2CenterX - SVG2 center X in design space (after transform)
 * @param {number} options.svg2CenterY - SVG2 center Y in design space (after transform)
 * @param {number} options.svg2Scale - SVG2 scale (after transform)
 * @returns {string|null} Merged path in absolute coordinates, or null if error
 */
export const mergeSvgPaths = ({
  svg1Content,
  svg2Content,
  p1,
  q1,
  p2,
  q2,
  svg1CenterX,
  svg1CenterY,
  svg1Scale,
  svg2CenterX,
  svg2CenterY,
  svg2Scale
}) => {
  try {
    // Get all paths from both SVGs
    const svg1Paths = getPathsFromSvg(svg1Content);
    const svg2Paths = getPathsFromSvg(svg2Content);
    
    // Find paths containing p1 and p2
    const path1Info = findPathContainingPoint(svg1Paths, p1);
    const path2Info = findPathContainingPoint(svg2Paths, p2);
    
    if (!path1Info || !path2Info) {
      console.error('Could not find paths containing p1 or p2');
      return null;
    }
    
    // Cut path1 at p1 (path1 ends at p1)
    const path1Cut = cutPathAtPoint(path1Info.pathInfo.d, p1, true);
    
    // Cut path2 from p2 (path2 starts at p2)
    // Keep everything from p2 onwards
    const path2Cut = cutPathFromPoint(path2Info.pathInfo.d, p2, true);
    
    // Convert to absolute coordinates
    const path1Absolute = convertPathToAbsolute(path1Cut, svg1CenterX, svg1CenterY, svg1Scale);
    const path2Absolute = convertPathToAbsolute(path2Cut, svg2CenterX, svg2CenterY, svg2Scale);
    
    // Merge paths (P1 = P2, so they connect directly)
    const mergedPath = mergePaths(path1Absolute, path2Absolute);
    
    return {
      mergedPath,
      path1: path1Absolute,
      path2: path2Absolute,
      path1Original: path1Cut,
      path2Original: path2Cut
    };
  } catch (error) {
    console.error('Error merging paths:', error);
    return null;
  }
};

